import React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRub, formatRuDate } from "@/lib/format";
import {
  ALL_ORGS,
  FIRMNESS_SHORT,
  filterEntries,
  orgLabel,
  sumDecimalStrings,
  weekLabel,
  type CashflowCellSelection,
} from "@/pages/cashflow/utils";
import type { CashflowEntry, CashflowOrg } from "@/types/api";

/** Подписи источников записей (ключи ставит бэк в `Entry.source`). */
const SOURCE_LABELS: Record<string, string> = {
  manual: "Ручная",
  loans: "Кредит 1С",
  payment_requests: "Заявка",
  payment_orders: "Платёжка",
  supplier_orders: "Заказ",
  planned_inflows: "План поступления",
};

const BADGE = "rounded px-1.5 py-0.5 text-xs whitespace-nowrap";

interface Props {
  selection: CashflowCellSelection;
  /** Подпись статьи из строки таблицы; `null` — итоговая строка (все статьи). */
  categoryLabel: string | null;
  entries: CashflowEntry[];
  orgs: CashflowOrg[];
  weeks: string[];
  /** ИНН выбранной вкладки; `ALL_ORGS` — без фильтра по организации. */
  orgInn: string;
  onClose: () => void;
}

export function EntriesDialog({
  selection,
  categoryLabel,
  entries,
  orgs,
  weeks,
  orgInn,
  onClose,
}: Props): React.ReactElement {
  const week = weeks[selection.weekIndex];
  // Недели нет в ответе — это рассинхрон (ниже баннер), а не «все недели».
  const rows =
    week === undefined
      ? []
      : filterEntries(entries, {
          week,
          category: selection.category,
          direction: selection.direction,
          orgInn,
        });

  const totalIn = sumDecimalStrings(rows.filter((e) => e.direction === "in").map((e) => e.amount));
  const totalOut = sumDecimalStrings(
    rows.filter((e) => e.direction === "out").map((e) => e.amount),
  );

  const title = [
    categoryLabel ?? "Все записи",
    week === undefined ? `неделя #${selection.weekIndex}` : `неделя ${weekLabel(week)}`,
    orgInn === ALL_ORGS ? "Все организации" : orgLabel(orgInn, orgs),
  ].join(" · ");

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Записи, из которых сложилась ячейка. Считаются на каждый запрос — в базе не
            хранятся.
          </DialogDescription>
        </DialogHeader>

        {week === undefined && (
          <p className="text-sm text-rose-600">
            В ответе бэка нет недели с номером {selection.weekIndex} — календарь и
            детализация разошлись, пересчитайте страницу.
          </p>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Записей нет</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Дата</TableHead>
                  <TableHead>Организация</TableHead>
                  <TableHead>Название</TableHead>
                  <TableHead>Источник</TableHead>
                  <TableHead>Твёрдость</TableHead>
                  <TableHead>Просрочка</TableHead>
                  <TableHead className="text-right">Сумма</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e, i) => (
                  <TableRow key={`${e.source}-${e.ref ?? "no-ref"}-${e.date}-${i}`}>
                    <TableCell className="whitespace-nowrap">{formatRuDate(e.date)}</TableCell>
                    <TableCell>{orgLabel(e.org_inn, orgs)}</TableCell>
                    <TableCell>{e.title}</TableCell>
                    <TableCell>
                      <span className={`${BADGE} bg-slate-100 text-slate-700`}>
                        {SOURCE_LABELS[e.source] ?? e.source}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`${BADGE} bg-sky-100 text-sky-800`}>
                        {FIRMNESS_SHORT[e.firmness] ?? e.firmness}
                      </span>
                    </TableCell>
                    <TableCell>
                      {e.overdue && (
                        <span className={`${BADGE} bg-rose-100 text-rose-700`}>Просрочено</span>
                      )}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums whitespace-nowrap ${
                        e.direction === "in" ? "text-emerald-700" : ""
                      }`}
                    >
                      {formatRub(e.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <p className="text-sm">
              Итого: приход{" "}
              <span className="tabular-nums font-medium text-emerald-700">
                {formatRub(totalIn)}
              </span>{" "}
              · расход <span className="tabular-nums font-medium">{formatRub(totalOut)}</span>
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
